import AlertsBanner from '../components/reports/AlertsBanner';
import { PageHeader, Stat, Stats, Panel } from '../components/office';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, purchaseOrdersApi, seedApi, marginApi, reportsIntelApi, shiftsApi, cashApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { BRAND } from '@verlo/shared/lib/brand';
import { qty } from '../lib/units';
import { dueToday, toChase } from '../lib/orders';
import { toast } from 'sonner';
import MorningBriefCard from '../components/reports/MorningBriefCard';
import VarianceBriefCard from '../components/reports/VarianceBriefCard';
import { useHasTill } from '../contexts/SiteContext';
import { ArrowUpRight, ChevronRight, Database } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

// What is due today (Back Office Review, wave C): the orders due in today, the orders to
// chase (not confirmed by their supplier's cut-off: Oct 2026, on its way), who is on shift,
// and what wants ordering (below par, less what's on order). Each opens the place to deal with it.
// What's below par, for the To order panel: the lines that have someone to order from, the
// ones at their low mark first, then the dearest.
const toOrderFrom = (data) => {
  const groups = (data?.groups || []).filter((g) => g.supplier_id);
  const lines = groups.flatMap((g) => g.lines.map((l) => ({ ...l, supplier_name: g.supplier_name, cost: l.quantity * l.price })))
    .sort((a, b) => (b.low ? 1 : 0) - (a.low ? 1 : 0) || b.cost - a.cost);
  return { lines, items: lines.length, suppliers: groups.length, total: groups.reduce((t, g) => t + g.total, 0) };
};

const DueToday = ({ toOrder }) => {
  const [due, setDue] = useState({ deliveries: null, chase: null, shifts: null });
  useEffect(() => {
    const today = new Date().toLocaleDateString('en-CA');   // the local day, YYYY-MM-DD
    Promise.allSettled([purchaseOrdersApi.list(), shiftsApi.getAll(today, today)]).then(([po, sh]) => {
      const orders = po.status === 'fulfilled' ? po.value.data || [] : [];
      const shifts = sh.status === 'fulfilled' ? sh.value.data || [] : [];
      setDue({ deliveries: dueToday(orders, today), chase: toChase(orders), drafts: orders.filter((o) => o.status === 'draft').length, shifts: shifts.length });
    });
  }, []);
  const names = (list) => [...new Set(list.map((o) => o.supplier_name))];
  const items = [
    { label: 'Deliveries today', value: due.deliveries?.length ?? null, to: '/back-office/orders', tone: due.deliveries?.length ? 'info' : null,
      hint: due.deliveries?.length ? names(due.deliveries).join(', ') : undefined },
    { label: 'To chase', value: due.chase?.length ?? null, to: '/back-office/orders', tone: due.chase?.length ? 'warn' : 'ok',
      hint: due.chase ? (due.chase.length ? (due.chase.length === 1 ? `${due.chase[0].supplier_name} hasn't confirmed` : `${names(due.chase).length} suppliers haven't confirmed`) : 'All confirmed') : undefined },
    { label: 'On shift today', value: due.shifts, to: '/back-office/rota', tone: due.shifts === 0 ? 'warn' : null, hint: due.shifts === 0 ? 'Nobody rota\'d' : undefined },
    // Nothing to order, but drafts not sent: those are what's waiting.
    toOrder?.items === 0 && due.drafts > 0
      ? { label: 'Wants ordering', value: 0, to: '/back-office/orders', tone: 'warn', hint: `${due.drafts} draft${due.drafts === 1 ? '' : 's'} to send` }
      : { label: 'Wants ordering', value: toOrder?.items ?? null, to: '/back-office/orders/new?from=par', tone: toOrder?.items > 0 ? 'warn' : 'ok', hint: toOrder ? (toOrder.items > 0 ? 'Below par, not on order' : 'All above par') : undefined },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="due-today">
      {items.map((it) => (
        <Stat key={it.label} label={it.label} value={it.value ?? '—'} to={it.to}
              delta={it.tone && it.hint ? { text: it.hint, tone: it.tone } : undefined} />
      ))}
    </div>
  );
};

const BackOfficeDashboard = () => {
  const navigate = useNavigate();
  const { hasPlatformPermission } = useAuth();
  const [stats, setStats] = useState(null);
  // Last night's cash-off: the newest closed drawer (cash-off wave 2). Its own read so a
  // deployment with no drawers just shows nothing here.
  const [lastCashUp, setLastCashUp] = useState(null);
  useEffect(() => {
    cashApi.sessions({ limit: 5 })
      .then(({ data }) => setLastCashUp((data?.sessions || []).find((x) => x.status === 'closed') || null))
      .catch(() => setLastCashUp(null));
  }, []);
  const [salesTrend, setSalesTrend] = useState([]);
  // To order (Oct 2026): what's below par, the measure ordering uses, so this and Orders agree.
  const [toOrder, setToOrder] = useState(null);
  const [weekComparison, setWeekComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [marginExpanded, setMarginExpanded] = useState(false);
  const [marginData, setMarginData] = useState(null);
  const [marginLoading, setMarginLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [briefsSiteId, setBriefsSiteId] = useState(null);
  // A back-office site has no floor on Verlo, so two of the tiles below have nothing of
  // its own to say: "Tables in use 0 / 0", and a last cash-off that belongs to a sibling
  // site. (The alert rows under them are org-wide by design and name the site they came
  // from — that is a separate question from this one.)
  const hasTill = useHasTill();

  // Fallback Margin Data (used when no sales data exists)
  const fallbackMarginData = {
    theoretical: 70,
    actual: 70,
    variance: 0,
    status: 'healthy',
    byCategory: [],
    insights: [{ type: 'info', message: 'No sales data available yet', suggestion: 'Make some orders to see margin analysis' }],
    trend: [],
    total_revenue: 0,
    total_cost: 0
  };

  const getVarianceColor = (variance) => {
    if (variance >= -5) return 'text-signal-ok';
    if (variance >= -10) return 'text-signal-warn';
    return 'text-signal-stop';
  };

  // Honest empty stats — an empty tenant reads as zero, never the old invented figures
  // (P5-DASH). The zeros-with-"No sales data yet" pattern mirrors fallbackMarginData above.
  const emptyStats = {
    total_sales_today: 0,
    total_sales_week: 0,
    total_sales_month: 0,
    orders_today: 0,
    orders_week: 0,
    avg_order_value: 0,
    tables_occupied: 0,
    total_tables: 0,
    staff_on_duty: 0,
    total_staff: 0,
  };

  const fetchData = async () => {
    setLoading(true);
    setMarginLoading(true);
    try {
      // Each promise wraps its own .catch so one endpoint's 404 (e.g. sales-trend)
      // doesn't take down the whole dashboard — including the AI briefs.
      const [statsRes, trendRes, stockRes, weekRes, sitesRes] = await Promise.all([
        dashboardApi.getStats().catch(() => ({ data: null })),
        dashboardApi.getSalesTrend(7).catch(() => ({ data: [] })),
        purchaseOrdersApi.suggestions().catch(() => ({ data: null })),
        dashboardApi.getWeekComparison().catch(() => ({ data: null })),
        reportsIntelApi.listSites().catch(() => ({ data: { sites: [] } })),
      ]);

      // Real data only — an empty endpoint yields an empty/zero state, never invented figures.
      setStats(statsRes.data || emptyStats);
      setSalesTrend(trendRes.data || []);
      setToOrder(toOrderFrom(stockRes.data));
      setWeekComparison(weekRes.data);
      // Default the AI briefs to the user's primary site (or first available).
      const sites = sitesRes.data?.sites || [];
      const primary = sites.find((s) => s.is_primary) || sites[0];
      if (primary?.id) setBriefsSiteId(primary.id);
    } catch (err) {
      // Belt-and-braces — Promise.all above should never reject now. Empty, not invented.
      setStats(emptyStats);
      setSalesTrend([]);
      setToOrder(toOrderFrom(null));
    } finally {
      setLoading(false);
    }

    // Fetch margin data separately
    try {
      const marginRes = await marginApi.getOverview(7);
      setMarginData(marginRes.data);
    } catch (err) {
      // Use fallback if no data
      setMarginData(fallbackMarginData);
    } finally {
      setMarginLoading(false);
    }
  };

  const loadCategoryProducts = async (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory(null);
      setCategoryProducts([]);
      return;
    }
    
    setSelectedCategory(categoryName);
    setProductsLoading(true);
    try {
      const res = await marginApi.getProductsByCategory(categoryName, 7);
      setCategoryProducts(res.data);
    } catch (err) {
      setCategoryProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedApi.seedDatabase();
      toast.success('Demo data seeded successfully!');
      fetchData();
    } catch (err) {
      toast.error("Couldn't seed data");
    } finally {
      setSeeding(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-80 rounded-lg lg:col-span-2" />
          <Skeleton className="h-80 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="back-office-dashboard">
      <PageHeader
        title="Today"
        purpose={`${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}. Last night in numbers, what is due, what needs a decision.`}
        // Seeding replaces an org's data wholesale: Verlo's own staff only (`orgs.manage`).
        actions={hasPlatformPermission('orgs.manage') ? (
          <Button variant="outline" onClick={handleSeedData} disabled={seeding} data-testid="seed-btn">
            <Database size={16} className="mr-2" />
            {seeding ? 'Seeding…' : 'Seed demo data'}
          </Button>
        ) : null}
      />

      {/* The four numbers */}
      <Stats>
        <Stat label="Revenue today" value={formatCurrency(stats?.total_sales_today)} testId="dash-stat-sales-today" />
        <Stat label="Orders today" value={stats?.orders_today || 0} hint={`Avg ${formatCurrency(stats?.avg_order_value)}`} />
        <Stat label="Low stock" value={stats?.low_stock_count || 0} delta={stats?.low_stock_count > 0 ? { text: 'Needs ordering', tone: 'warn' } : undefined} />
        {/* The server counts them as `occupied_tables` (routes/reports.py); this read
            `active_tables`, which it never sent, so the tile always said 0 (Oct 2026). */}
        {hasTill && <Stat label="Tables in use" value={`${stats?.occupied_tables || 0} / ${stats?.total_tables || 0}`} hint={`${Math.round(((stats?.occupied_tables || 0) / (stats?.total_tables || 1)) * 100)}% in use`} />}
        {hasTill && lastCashUp && (() => {
          const v = Math.round((lastCashUp.variance || 0) * 100);
          const tone = v === 0 ? 'ok' : Math.abs(v) > 500 ? 'stop' : 'warn';
          const text = v === 0 ? 'Balanced' : `${v > 0 ? 'Over' : 'Short'} ${formatCurrency(Math.abs(lastCashUp.variance))}`;
          return (
            <Stat label="Last cash-off" value={formatCurrency(lastCashUp.declared_cash)} delta={{ text, tone }}
              hint={`${lastCashUp.terminal_label || lastCashUp.terminal_id} · Z ${lastCashUp.z_number}`}
              to={`/back-office/reports/cash-ups/${lastCashUp.id}`} testId="dash-stat-cash-off" />
          );
        })()}
      </Stats>

      {/* What needs a decision: the operational alerts, then what is due today. */}
      <AlertsBanner site={briefsSiteId || undefined} />
      <DueToday toOrder={toOrder} />

      {/* The AI briefs, when this deployment has them — a configured key, a real brief. */}
      {briefsSiteId && (
        <>
          <VarianceBriefCard site={briefsSiteId} />
          <MorningBriefCard site={briefsSiteId} range="wtd" />
        </>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Panel className="lg:col-span-2" title="The last seven days" purpose="Sales each day."
          right={<Button variant="ghost" size="sm" className="text-ink-soft" onClick={() => navigate('/back-office/reports')}>Reports<ArrowUpRight size={14} className="ml-1" /></Button>}>
          <div className="h-64 px-3 py-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesTrend}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BRAND.verlo} stopOpacity={0.14} />
                    <stop offset="95%" stopColor={BRAND.verlo} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={BRAND.hairline} vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDate} stroke={BRAND.inkFaint} fontSize={12} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `£${v}`} stroke={BRAND.inkFaint} fontSize={12} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [formatCurrency(v), 'Sales']} labelFormatter={formatDate}
                  contentStyle={{ borderRadius: 8, border: `1px solid ${BRAND.hairline}` }} />
                <Area type="monotone" dataKey="sales" stroke={BRAND.verlo} strokeWidth={2} fill="url(#salesGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="To order" testId="today-to-order"
          purpose={!toOrder ? 'Reading what\'s below par…' : toOrder.items
            ? `${toOrder.items} item${toOrder.items === 1 ? '' : 's'} below par from ${toOrder.suppliers} supplier${toOrder.suppliers === 1 ? '' : 's'} · about £${Math.round(toOrder.total).toLocaleString('en-GB')}`
            : 'Everything is at or above par.'}
          right={toOrder?.items > 0 && <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/back-office/orders/new?from=par')} data-testid="today-review-order">Review and order</Button>}>
          <div className="divide-y divide-hairline">
            {(toOrder?.lines || []).slice(0, 4).map((l) => (
              <div key={l.stock_id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-[14px] text-ink"><span className="truncate">{l.name}</span>{l.low && <Signal tone="stop" dot={false} className="shrink-0">low</Signal>}</p>
                  <p className="text-[12px] text-ink-faint">{l.supplier_name}</p>
                </div>
                <span className="shrink-0 font-mono text-[13px] tabular-nums text-ink-soft">{qty(l.have, '')} of {qty(l.par, l.unit)}</span>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 text-[13px] text-ink-soft">
            {toOrder?.lines.length > 4 && <>and {toOrder.lines.length - 4} more · </>}
            <button type="button" className="text-verlo hover:underline" onClick={() => navigate('/back-office/stock')}>All stock levels</button>
          </div>
        </Panel>
      </div>

      {weekComparison && (
        <section aria-label="This week against last" data-testid="week-comparison-card">
          <p className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">This week against last</p>
          <Stats>
            {[
              { label: 'Revenue', now: formatCurrency(weekComparison.this_week?.revenue), then: formatCurrency(weekComparison.last_week?.revenue), ch: weekComparison.change?.revenue },
              { label: 'Orders', now: weekComparison.this_week?.order_count || 0, then: weekComparison.last_week?.order_count || 0, ch: weekComparison.change?.orders },
              { label: 'Tips', now: formatCurrency(weekComparison.this_week?.tips), then: formatCurrency(weekComparison.last_week?.tips), ch: weekComparison.change?.tips },
            ].map((w) => (
              // Nothing taken yet this week isn't a 100% fall (Chris's review): it says so.
              <Stat key={w.label} label={w.label} value={w.now} hint={`Same point last week ${w.then}`}
                delta={!weekComparison.this_week?.order_count ? { text: 'No trading yet', tone: null }
                  : w.ch ? { text: `${w.ch > 0 ? '+' : ''}${w.ch}%`, tone: w.ch > 0 ? 'ok' : 'stop' } : undefined} />
            ))}
          </Stats>
        </section>
      )}

      {/* A "Latest orders" list sat here. Orders are the till's (`/orders` is mounted on the
          node only), so in the Cloud it could only ever say none; Sales has the takings. */}
      {/* Margin: what you expected to make against what you made, last seven days. */}
      {marginLoading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : marginData ? (
        <Panel title="Margin, last seven days"
          purpose={marginData.total_revenue > 0 ? `On ${formatCurrency(marginData.total_revenue)} of sales.` : 'Are you making money right now?'}
          right={marginData.total_revenue > 0 && <Signal tone={marginData.status === 'healthy' ? 'ok' : marginData.status === 'warning' ? 'warn' : 'stop'}>
            {marginData.status === 'healthy' ? 'healthy' : marginData.status === 'warning' ? 'worth a look' : 'margin slipping'}</Signal>}>
          {/* No sales, no margin: it said "Made −3%" over "Analysed 0 orders" (Chris's review). */}
          {!(marginData.total_revenue > 0) ? (
            <p className="px-5 py-4 text-[13.5px] text-ink-soft" data-testid="margin-no-sales">No sales in the last seven days to work a margin out from.</p>
          ) : (
          <div className="space-y-4 px-5 py-4">
            <Stats>
              <Stat label="Expected" value={`${marginData.theoretical}%`} hint="From the recipes" />
              <Stat label="Made" value={`${marginData.actual}%`} />
              <Stat label="Gap" value={`${marginData.variance > 0 ? '+' : ''}${marginData.variance}%`}
                delta={marginData.variance < -5 ? { text: 'To close', tone: marginData.variance < -10 ? 'stop' : 'warn' } : undefined} />
              <Stat label="Waste" value={formatCurrency(marginData.wastage_cost || 0)} hint="This week" to="/back-office/waste" />
            </Stats>

            {marginData.insights?.length > 0 && (
              <Notice tone={marginData.insights[0].type === 'alert' ? 'stop' : marginData.insights[0].type === 'warning' ? 'warn' : 'info'} word="Margin" title={marginData.insights[0].message}>
                {marginData.insights[0].suggestion}
              </Notice>
            )}

            {marginData.byCategory?.length > 0 && (
              <button type="button" className="flex w-full items-center justify-between rounded-md px-2 py-2 text-[13.5px] text-ink-soft hover:bg-hover-tint hover:text-ink"
                onClick={() => setMarginExpanded(!marginExpanded)} aria-expanded={marginExpanded}>
                By category <ChevronRight className={`transition-transform ${marginExpanded ? 'rotate-90' : ''}`} size={16} />
              </button>
            )}

            {marginExpanded && marginData.byCategory?.length > 0 && (
              <div className="divide-y divide-hairline rounded-lg border border-hairline">
                {marginData.byCategory.map((cat) => (
                  <div key={cat.name}>
                    <button type="button" className="w-full px-4 py-3 text-left hover:bg-hover-tint" onClick={() => loadCategoryProducts(cat.name)} aria-expanded={selectedCategory === cat.name}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-[14px] text-ink">
                          <ChevronRight size={14} className={`text-ink-faint transition-transform ${selectedCategory === cat.name ? 'rotate-90' : ''}`} />{cat.name}
                          {cat.revenue > 0 && <span className="font-mono text-[12px] text-ink-faint">{formatCurrency(cat.revenue)}</span>}
                        </span>
                        <span className={`font-mono text-[13px] tabular-nums ${getVarianceColor(cat.variance)}`}>{cat.variance > 0 ? '+' : ''}{cat.variance}%</span>
                      </div>
                      <div className="mt-2 flex items-center gap-3 pl-6">
                        <div className="h-1.5 flex-1 rounded-full bg-hairline">
                          <div className={`h-1.5 rounded-full ${cat.variance >= -5 ? 'bg-signal-ok-line' : cat.variance >= -10 ? 'bg-signal-warn-line' : 'bg-signal-stop-line'}`}
                            style={{ width: `${Math.min((cat.actual / cat.theoretical) * 100, 100)}%` }} />
                        </div>
                        <span className="text-[12px] text-ink-faint">{cat.actual}% of {cat.theoretical}%</span>
                      </div>
                    </button>
                    {selectedCategory === cat.name && (
                      <div className="space-y-1 bg-surface-2 px-4 py-2 pl-10">
                        {productsLoading ? <p className="py-2 text-[13px] text-ink-soft">Loading…</p>
                          : categoryProducts.length ? categoryProducts.map((p) => (
                            <div key={p.id} className="flex items-center justify-between gap-3 py-1.5 text-[13px]">
                              <span className="min-w-0 truncate text-ink">{p.name} <span className="text-ink-faint">· {p.quantity_sold} sold · {formatCurrency(p.revenue)}</span></span>
                              <span className={`font-mono tabular-nums ${getVarianceColor(p.variance)}`}>{p.variance > 0 ? '+' : ''}{p.variance}%</span>
                            </div>
                          )) : <p className="py-2 text-[13px] text-ink-soft">No product data for this category.</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {marginExpanded && marginData.trend?.length > 0 && (
              <div>
                <p className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Each day: expected against made</p>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={marginData.trend} barGap={4}>
                      <CartesianGrid stroke={BRAND.hairline} vertical={false} />
                      <XAxis dataKey="period" stroke={BRAND.inkFaint} fontSize={11} axisLine={false} tickLine={false} />
                      <YAxis domain={[40, 80]} stroke={BRAND.inkFaint} fontSize={11} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(v, name) => [`${v}%`, name === 'theoretical' ? 'Expected' : 'Made']} contentStyle={{ borderRadius: 8, border: `1px solid ${BRAND.hairline}` }} />
                      <Bar dataKey="theoretical" fill={BRAND.hairline} radius={[3, 3, 0, 0]} name="Expected" />
                      <Bar dataKey="actual" fill={BRAND.verlo} radius={[3, 3, 0, 0]} name="Made" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
          )}
        </Panel>
      ) : null}
    </div>
  );
};

export default BackOfficeDashboard;
